import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { 
    stcBinGrpRelationService, 
    StcBinGrpRelationDto
} from '../../../../../services/stcBinGrpRelationService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../../services/stcBinGrpService';

interface SelectedRows {
    [key: string]: boolean;
}

interface BinGrpRelationSidePanelProps {
    selectedRows: SelectedRows;
    data: StcBinGrpRelationDto[];
    onClose: () => void;
    onSave: () => void;
}

export const BinGrpRelationSidePanel: React.FC<BinGrpRelationSidePanelProps> = ({
    selectedRows,
    data,
    onClose,
    onSave
}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);

    const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;
    const selectedData = data.filter(item => selectedRows[item.id.toString()]);

    useEffect(() => {
        loadBinGroups();
    }, []);

    const loadBinGroups = async () => {
        try {
            const groups = await stcBinGrpService.getCurrent();
            setBinGroups(groups);
        } catch (err) {
            console.error('Failed to load bin groups', err);
        }
    };

    const getBinGrpDescription = (binGrpNo: number): string => {
        const group = binGroups.find(g => g.binGrpNo === binGrpNo);
        return group?.description1 || '';
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');
            
            // Delete each selected item individually since we don't have bulk delete API
            const deletePromises = selectedData.map(item => 
                stcBinGrpRelationService.delete(item.companyNo, item.masterBinGrpNo, item.childBinGrpNo)
            );
            
            await Promise.all(deletePromises);
            
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved sletting');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />
            
            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Massesletting</h2>
                        <p className="text-sm text-gray-600">
                            {selectedRowCount} {selectedRowCount === 1 ? 'relasjon' : 'relasjoner'} valgt i Lokasjonsgrupperelasjon
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-red-800">
                                <p className="font-medium mb-1">Massesletting</p>
                                <p>Du er i ferd med å slette {selectedRowCount} {selectedRowCount === 1 ? 'relasjon' : 'relasjoner'}. Denne handlingen kan ikke angres.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">Merk</p>
                                <p>Lokasjonsgrupperelasjon kan ikke redigeres. Du kan bare opprette nye eller slette eksisterende relasjoner.</p>
                            </div>
                        </div>
                    </div>

                    {/* Selected Items List */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-900">Relasjoner som vil bli slettet:</h3>
                        <div className="max-h-80 overflow-y-auto space-y-2">
                            {selectedData.map(item => (
                                <div key={item.id} className="text-xs bg-white p-3 rounded border border-gray-200">
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-700">Master:</span>
                                            <span className="text-gray-900">
                                                {item.masterBinGrpNo} - {getBinGrpDescription(item.masterBinGrpNo)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-700">Barn:</span>
                                            <span className="text-gray-900">
                                                {item.childBinGrpNo} - {getBinGrpDescription(item.childBinGrpNo)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Avbryt
                        </button>
                        
                        <button
                            onClick={handleBulkDelete}
                            disabled={isDeleting}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                isDeleting
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                        >
                            {isDeleting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Sletter...
                                </>
                            ) : showSuccess ? (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    Slettet!
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    Slett {selectedRowCount} {selectedRowCount === 1 ? 'relasjon' : 'relasjoner'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
